import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { isApiErrorHandled } from "@/services/api";
import { authQueryKeys } from "@/hooks/useAuth";

import {
  getAppointmentsService,
  getAppointmentAttendeesService,
  postAppointmentService,
  patchAppointmentService,
  cancelAppointmentService,
  addDocumentService,
  addParticipantService,
  addToGalleryService,
  archiveDealService,
  cancelDealService,
  createDealService,
  createTaskService,
  deleteDealService,
  deleteDocumentService,
  updateDocumentService,
  deleteTaskService,
  reorderTasksService,
  getActivityService,
  getDealClientSummaryService,
  getDealEmailsService,
  getDealService,
  getDealsExportService,
  getDealsService,
  getDealStatsService,
  getDocumentsService,
  getMyDealsService,
  getPublicDealService,
  getTasksService,
  postActivityService,
  removeFromGalleryService,
  unarchiveDealService,
  updateDealService,
  updateTaskService,
  nudgeTaskService,
} from "@/services/deals.service";
import { IAPIResponse } from "@/services/api.types";
import type { Audience } from "@/enum/common.enum";
import { BoardColumn, DealPhase } from "@/enum/common.enum";
import {
  Appointment,
  AppointmentAttendee,
  AppointmentPayload,
  Activity,
  ActivityFormData,
  ClientSummaryResponse,
  Deal,
  DealDetail,
  DealEmailListResponse,
  DealFormData,
  DealExportResponse,
  DealListParams,
  DealListResponse,
  DealStats,
  DealUpdatePayload,
  DealDocument,
  DocumentFormData,
  GalleryAddFormData,
  MyDealsResponse,
  ParticipantFormData,
  PublicDealResponse,
  Task,
  TaskFormData,
  TaskListParams,
  TaskListResponse,
  TaskUpdatePayload,
} from "@/types/deals.types";

export const dealsQueryKeys = {
  all: () => ["deals"] as const,
  lists: () => [...dealsQueryKeys.all(), "list"] as const,
  list: (params: DealListParams) => [...dealsQueryKeys.lists(), params] as const,
  details: () => [...dealsQueryKeys.all(), "detail"] as const,
  detail: (dealId: string) => [...dealsQueryKeys.details(), dealId] as const,
  stats: (dealId: string) => [...dealsQueryKeys.all(), "stats", dealId] as const,
  export: (params: DealListParams) => [...dealsQueryKeys.all(), "export", params] as const,
  clientSummary: (dealId: string) => [...dealsQueryKeys.all(), "client-summary", dealId] as const,
  tasks: (dealId: string, params: TaskListParams = {}) =>
    [...dealsQueryKeys.all(), "tasks", dealId, params] as const,
  documents: (dealId: string) => [...dealsQueryKeys.all(), "documents", dealId] as const,
  activity: (dealId: string) => [...dealsQueryKeys.all(), "activity", dealId] as const,
  emails: (dealId: string, page: number) => [...dealsQueryKeys.all(), "emails", dealId, page] as const,
  appointments: (dealId: string, includePast: boolean) => [...dealsQueryKeys.all(), "appointments", dealId, includePast] as const,
  appointmentAttendees: (dealId: string) => [...dealsQueryKeys.all(), "appointment-attendees", dealId] as const,
  public: (slug: string) => [...dealsQueryKeys.all(), "public", slug] as const,
  mine: () => [...dealsQueryKeys.all(), "mine"] as const,
};

// ============ Deals: queries ============
/** Returns `{pagination, deals}` — read `data?.deals` for the rows. */
export const useGetDeals = (params: DealListParams = {}) => {
  return useQuery<DealListResponse, Error>({
    queryKey: dealsQueryKeys.list(params),
    queryFn: () => getDealsService(params),
  });
};

/** Header-strip numbers for a deal. Separate from `useGetDeal` because it is
 *  computed over the task/document/activity sets, not the deal document. */
export const useGetDealStats = (dealId: string, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<DealStats, Error>({
    queryKey: dealsQueryKeys.stats(dealId),
    queryFn: () => getDealStatsService(dealId),
    enabled: enabled && !!dealId,
  });
};

/** Disabled by default — fetched on demand when Export is clicked. */
export const useGetDealsExport = (params: DealListParams = {}, { enabled = false } = {}) => {
  return useQuery<DealExportResponse, Error>({
    queryKey: dealsQueryKeys.export(params),
    queryFn: () => getDealsExportService(params),
    enabled,
    gcTime: 0,
  });
};

export const useGetDeal = (dealId: string, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<DealDetail, Error>({
    queryKey: dealsQueryKeys.detail(dealId),
    queryFn: () => getDealService(dealId),
    enabled: enabled && !!dealId,
  });
};

export const useGetDealClientSummary = (dealId: string, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<ClientSummaryResponse, Error>({
    queryKey: dealsQueryKeys.clientSummary(dealId),
    queryFn: () => getDealClientSummaryService(dealId),
    enabled: enabled && !!dealId,
  });
};

export const useGetDealEmails = (dealId: string, page = 1, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<DealEmailListResponse, Error>({
    queryKey: dealsQueryKeys.emails(dealId, page),
    queryFn: () => getDealEmailsService(dealId, page),
    enabled: enabled && !!dealId,
  });
};

/** The deals this account was invited into. Distinct from `useGetDeals`, which
 *  answers "the deals my organization runs" and returns full documents. */
export const useGetMyDeals = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<MyDealsResponse, Error>({
    queryKey: dealsQueryKeys.mine(),
    queryFn: getMyDealsService,
    enabled,
  });
};

export const useGetPublicDeal = (slug: string, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<PublicDealResponse, Error>({
    queryKey: dealsQueryKeys.public(slug),
    queryFn: () => getPublicDealService(slug),
    enabled: enabled && !!slug,
  });
};

// ============ Deals: mutations ============
/** Every change that moves a deal list also moves the sidebar's deal badge and
 *  plan card, which read `/me/context` — otherwise they lag until the 90s poll. */
const invalidateDealLists = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: dealsQueryKeys.lists() });
  queryClient.invalidateQueries({ queryKey: authQueryKeys.meContext() });
};

export const usePostDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<Deal>, AxiosError, DealFormData>({
    mutationFn: createDealService,
    onSuccess: res => {
      toast.success(res.message);
      invalidateDealLists(queryClient);
    },
    onError: err => toast.error(err.message),
  });
};

/** No success toast: every edit here — phase advance, contract terms, share
 *  toggle, snapshot — is immediately visible in the surface that triggered it,
 *  so the confirmation was pure noise. Failures still toast. */
export const usePatchDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<Deal>, AxiosError, { dealId: string; payload: DealUpdatePayload }>({
    mutationFn: ({ dealId, payload }) => updateDealService(dealId, payload),
    onSuccess: (_res, { dealId }) => {
      invalidateDealLists(queryClient);
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.activity(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

/**
 * Board drag-and-drop: move a deal to another phase.
 *
 * Distinct from `usePatchDeal` in that the card has to move columns *now* —
 * waiting for the round-trip means the card snaps back under the cursor and
 * then jumps, which reads as a failed drag. So the deal list caches are patched
 * optimistically and rolled back if the PATCH fails.
 *
 * The patch writes `board_column` as well as `phase` because `board_column` is
 * a server-side derivation; writing `phase` alone would leave the card in its
 * old column until the refetch landed. `onSettled` re-invalidates either way,
 * so the server stays the authority on both fields.
 */
export const useMoveDealPhase = () => {
  const queryClient = useQueryClient();
  return useMutation<
    IAPIResponse<Deal>,
    AxiosError,
    { dealId: string; phase: DealPhase; boardColumn: BoardColumn },
    { previous: Array<[QueryKey, DealListResponse | undefined]> }
  >({
    mutationFn: ({ dealId, phase }) => updateDealService(dealId, { phase } as DealUpdatePayload),
    onMutate: async ({ dealId, phase, boardColumn }) => {
      // In-flight list refetches would otherwise land on top of the optimistic
      // write and put the card back where it started.
      await queryClient.cancelQueries({ queryKey: dealsQueryKeys.lists() });
      const previous = queryClient.getQueriesData<DealListResponse>({
        queryKey: dealsQueryKeys.lists(),
      });
      previous.forEach(([key, data]) => {
        if (!data?.deals) return;
        queryClient.setQueryData<DealListResponse>(key, {
          ...data,
          deals: data.deals.map((deal) =>
            deal.id === dealId ? { ...deal, phase, board_column: boardColumn } : deal
          ),
        });
      });
      return { previous };
    },
    onError: (err, _variables, ctx) => {
      ctx?.previous.forEach(([key, data]) => queryClient.setQueryData<DealListResponse>(key, data));
      toast.error(err.message);
    },
    // No success toast — the card has already moved under the cursor.
    onSuccess: (_res, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.activity(dealId) });
    },
    onSettled: () => {
      invalidateDealLists(queryClient);
    },
  });
};

export const useArchiveDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean; archived_at: string }>, AxiosError, string>({
    mutationFn: archiveDealService,
    onSuccess: (res, dealId) => {
      toast.success(res.message);
      invalidateDealLists(queryClient);
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

export const useCancelDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean; cancelled_at: string }>, AxiosError, string>({
    mutationFn: cancelDealService,
    onSuccess: (_res, dealId) => {
      toast.success("Deal cancelled and moved to Archived");
      invalidateDealLists(queryClient);
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

export const useUnarchiveDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean }>, AxiosError, string>({
    mutationFn: unarchiveDealService,
    onSuccess: (res, dealId) => {
      toast.success(res.message);
      invalidateDealLists(queryClient);
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

export const useDeleteDeal = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean }>, AxiosError, { dealId: string; confirm: string }>({
    mutationFn: ({ dealId, confirm }) => deleteDealService(dealId, confirm),
    onSuccess: (res, { dealId }) => {
      toast.success(res.message);
      invalidateDealLists(queryClient);
      queryClient.removeQueries({ queryKey: dealsQueryKeys.detail(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

// ============ Gallery ============
export const useAddToGallery = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean; url: string }>, AxiosError, { dealId: string; payload: GalleryAddFormData }>({
    mutationFn: ({ dealId, payload }) => addToGalleryService(dealId, payload),
    // The photo appearing in the gallery is the confirmation.
    onSuccess: (_res, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

export const useRemoveFromGallery = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean }>, AxiosError, { dealId: string; url: string }>({
    mutationFn: ({ dealId, url }) => removeFromGalleryService(dealId, url),
    // The photo disappearing is the confirmation.
    onSuccess: (_res, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

// ============ Participants ============
export const usePostParticipant = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean }>, AxiosError, { dealId: string; payload: ParticipantFormData }>({
    mutationFn: ({ dealId, payload }) => addParticipantService(dealId, payload),
    onSuccess: (res, { dealId }) => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

// ============ Tasks ============
/** Returns `{pagination, tasks}` — read `data?.tasks` for the rows.
 *  `params` carries the server-side search / phase filter. */
export const useGetTasks = (
  dealId: string,
  { enabled = true, params = {} }: { enabled?: boolean; params?: TaskListParams } = {}
) => {
  return useQuery<TaskListResponse, Error>({
    queryKey: dealsQueryKeys.tasks(dealId, params),
    queryFn: () => getTasksService(dealId, params),
    enabled: enabled && !!dealId,
  });
};

export const usePostTask = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<Task>, AxiosError, { dealId: string; payload: TaskFormData }>({
    mutationFn: ({ dealId, payload }) => createTaskService(dealId, payload),
    onSuccess: (res, { dealId }) => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: [...dealsQueryKeys.all(), "tasks", dealId] });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.stats(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

/** Optimistic, because a checkmark that waits for the round-trip reads as lag.
 *
 *  The write lands on every cached task list for this deal — the timeline's
 *  search/phase params are part of the query key, so there is more than one.
 *  Nothing is toasted on success: this fires on every checkbox click, and a
 *  confirmation per click was the "OK popup" fatigue the client reported. The
 *  row itself is the confirmation. Errors still toast, and the rollback is what
 *  makes that honest. */
export const usePatchTask = () => {
  const queryClient = useQueryClient();
  return useMutation<
    IAPIResponse<Task>,
    AxiosError,
    { taskId: string; dealId: string; payload: TaskUpdatePayload },
    { previous: Array<[QueryKey, TaskListResponse | undefined]> }
  >({
    mutationFn: ({ taskId, payload }) => updateTaskService(taskId, payload),
    onMutate: async ({ taskId, dealId, payload }) => {
      const tasksKey = [...dealsQueryKeys.all(), "tasks", dealId];
      // An in-flight list refetch would otherwise land on top of the optimistic
      // write and flip the checkbox back.
      await queryClient.cancelQueries({ queryKey: tasksKey });
      const previous = queryClient.getQueriesData<TaskListResponse>({ queryKey: tasksKey });
      previous.forEach(([key, data]) => {
        if (!data?.tasks) return;
        queryClient.setQueryData<TaskListResponse>(key, {
          ...data,
          // Cast: `TaskUpdatePayload.assigned_role` is the wider write-side
          // union. This is a preview of the row, and `onSettled` re-reads the
          // server's own version regardless.
          tasks: data.tasks.map((task) =>
            task.id === taskId ? ({ ...task, ...payload } as Task) : task
          ),
        });
      });
      return { previous };
    },
    onError: (err, _variables, ctx) => {
      ctx?.previous.forEach(([key, data]) => queryClient.setQueryData<TaskListResponse>(key, data));
      toast.error(err.message);
    },
    // Both on success and after a rollback: the server stays the authority for
    // the row, and the header strip's progress/overdue counts are derived from
    // it, so they have to re-read too.
    onSettled: (_res, _err, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: [...dealsQueryKeys.all(), "tasks", dealId] });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.stats(dealId) });
      // A status flip logs an activity row server-side (`Completed: X`), and
      // every portal's Recent Activity rail reads this same key — without the
      // invalidation it only caught up on the next unrelated refetch.
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.activity(dealId) });
    },
  });
};

/** Drag-to-reorder. The dragged rows trade the `sort_order` slots they already
 *  hold, so the optimistic preview does the same and the list never jumps. */
export const useReorderTasks = () => {
  const queryClient = useQueryClient();
  return useMutation<
    IAPIResponse<{ ok: boolean; changed: number }>,
    AxiosError,
    { dealId: string; taskIds: string[] },
    { previous: Array<[QueryKey, TaskListResponse | undefined]> }
  >({
    mutationFn: ({ dealId, taskIds }) => reorderTasksService(dealId, taskIds),
    onMutate: async ({ dealId, taskIds }) => {
      const tasksKey = [...dealsQueryKeys.all(), "tasks", dealId];
      await queryClient.cancelQueries({ queryKey: tasksKey });
      const previous = queryClient.getQueriesData<TaskListResponse>({ queryKey: tasksKey });
      previous.forEach(([key, data]) => {
        if (!data?.tasks) return;
        const moved = data.tasks.filter((task) => taskIds.includes(task.id));
        const slots = moved.map((task) => task.sort_order ?? 0).sort((a, b) => a - b);
        const slotFor = new Map(taskIds.map((id, index) => [id, slots[index]]));
        const tasks = data.tasks
          .map((task) => (slotFor.has(task.id) ? { ...task, sort_order: slotFor.get(task.id) } : task))
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        queryClient.setQueryData<TaskListResponse>(key, { ...data, tasks });
      });
      return { previous };
    },
    onError: (err, _variables, ctx) => {
      ctx?.previous.forEach(([key, data]) => queryClient.setQueryData<TaskListResponse>(key, data));
      toast.error(err.message);
    },
    onSettled: (_res, _err, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: [...dealsQueryKeys.all(), "tasks", dealId] });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
    },
  });
};

export const useNudgeTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dealId, taskId, message }: { dealId: string; taskId: string; message?: string }) => nudgeTaskService(dealId, taskId, message),
    onSuccess: (res, { dealId }) => {
      toast.success(`Reminder sent to ${res.data?.owner || "the task owner"}`);
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.activity(dealId) });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || err.message || "Could not send reminder"),
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean }>, AxiosError, { taskId: string; dealId: string }>({
    mutationFn: ({ taskId }) => deleteTaskService(taskId),
    onSuccess: (res, { dealId }) => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: [...dealsQueryKeys.all(), "tasks", dealId] });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.stats(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

// ============ Documents ============
export const useGetDocuments = (dealId: string, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<DealDocument[], Error>({
    queryKey: dealsQueryKeys.documents(dealId),
    queryFn: () => getDocumentsService(dealId),
    enabled: enabled && !!dealId,
  });
};

export const usePostDocument = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<DealDocument>, AxiosError, { dealId: string; payload: DocumentFormData }>({
    mutationFn: ({ dealId, payload }) => addDocumentService(dealId, payload),
    onSuccess: (res, { dealId }) => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.documents(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.stats(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.activity(dealId) });
    },
  });
};

export const usePatchDocument = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<DealDocument>, AxiosError, { docId: string; dealId: string; audience: Audience }>({
    mutationFn: ({ docId, audience }) => updateDocumentService(docId, { audience }),
    onSuccess: (_res, { dealId }) => {
      toast.success("Sharing updated");
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.documents(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.activity(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean }>, AxiosError, { docId: string; dealId: string }>({
    mutationFn: ({ docId }) => deleteDocumentService(docId),
    onSuccess: (res, { dealId }) => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.documents(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.stats(dealId) });
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.activity(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

// ============ Showings & meetings ============
export const useGetAppointments = (dealId: string, includePast = false, { enabled = true }: { enabled?: boolean } = {}) =>
  useQuery<Appointment[], Error>({
    queryKey: dealsQueryKeys.appointments(dealId, includePast),
    queryFn: () => getAppointmentsService(dealId, includePast),
    enabled: enabled && !!dealId,
  });

const useInvalidateAppointments = () => {
  const queryClient = useQueryClient();
  return (dealId: string) => {
    queryClient.invalidateQueries({ queryKey: [...dealsQueryKeys.all(), "appointments", dealId] });
    queryClient.invalidateQueries({ queryKey: dealsQueryKeys.activity(dealId) });
    queryClient.invalidateQueries({ queryKey: [...dealsQueryKeys.all(), "emails", dealId] });
    queryClient.invalidateQueries({ queryKey: dealsQueryKeys.stats(dealId) });
  };
};

export const usePostAppointment = () => {
  const invalidate = useInvalidateAppointments();
  return useMutation({
    mutationFn: ({ dealId, payload }: { dealId: string; payload: AppointmentPayload }) => postAppointmentService(dealId, payload),
    onSuccess: (_r, { dealId }) => { invalidate(dealId); toast.success("Appointment scheduled"); },
    onError: appointmentError,
  });
};

export const usePatchAppointment = () => {
  const invalidate = useInvalidateAppointments();
  return useMutation({
    mutationFn: ({ appointmentId, payload }: { appointmentId: string; dealId: string; payload: AppointmentPayload }) =>
      patchAppointmentService(appointmentId, payload),
    onSuccess: (_r, { dealId }) => { invalidate(dealId); toast.success("Appointment updated"); },
    onError: appointmentError,
  });
};

export const useCancelAppointment = () => {
  const invalidate = useInvalidateAppointments();
  return useMutation({
    mutationFn: ({ appointmentId, expectedSequence }: { appointmentId: string; dealId: string; expectedSequence: number }) => cancelAppointmentService(appointmentId, expectedSequence),
    onSuccess: (_r, { dealId }) => { invalidate(dealId); toast.success("Appointment cancelled"); },
    onError: appointmentError,
  });
};

function appointmentError(error: Error) {
  if (!isApiErrorHandled(error)) toast.error(error.message);
}

export const useGetAppointmentAttendees = (dealId: string, enabled = true) =>
  useQuery<AppointmentAttendee[], Error>({
    queryKey: dealsQueryKeys.appointmentAttendees(dealId),
    queryFn: () => getAppointmentAttendeesService(dealId),
    enabled: enabled && !!dealId,
  });

// ============ Activity ============
export const useGetActivity = (dealId: string, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<Activity[], Error>({
    queryKey: dealsQueryKeys.activity(dealId),
    queryFn: () => getActivityService(dealId),
    enabled: enabled && !!dealId,
  });
};

export const usePostActivity = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean }>, AxiosError, { dealId: string; payload: ActivityFormData }>({
    mutationFn: ({ dealId, payload }) => postActivityService(dealId, payload),
    // The note appearing at the top of the feed is the confirmation.
    onSuccess: (_res, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: dealsQueryKeys.activity(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};
